'use client'

import { useAtomValue } from 'jotai'
import { useMemo } from 'react'

import { useHomeTradeHistory } from '@/features/home/hooks/use-home-trade-stats'
import { buildJournal, type TradeJournal } from '@/features/home/lib/home-trade-journal'
import { positionsRawAtom } from '@/state/account'
import { OrderSide } from '@/types/generated/common/trading'

/**
 * The last 30 days of the user's fills paired into complete trades, with the numbers and
 * habits a coach would look at (features/home/lib/home-trade-journal.ts). Open positions
 * anchor the pairing: what's open now tells which fills were closing an older trade.
 */
export function useHomeTradeJournal(): {
  journal: TradeJournal | null
  isPending: boolean
  hasAccounts: boolean
} {
  const { trades, instrumentOf, isPending, hasAccounts } = useHomeTradeHistory()
  const positions = useAtomValue(positionsRawAtom)

  const journal = useMemo(() => {
    if (!trades) return null
    const open = new Map<string, number>()
    for (const position of positions) {
      const quantity = Math.abs(Number(position.quantity) || 0)
      const signed = position.side === OrderSide.ORDER_SIDE_SELL ? -quantity : quantity
      const key = `${position.accountId}|${position.exchange}|${position.symbol}`
      open.set(key, (open.get(key) ?? 0) + signed)
    }
    return buildJournal({
      trades,
      instrumentOf,
      currentPositionOf: (accountId, exchange, symbol) =>
        open.get(`${accountId}|${exchange}|${symbol}`) ?? 0,
    })
  }, [trades, instrumentOf, positions])

  return { journal, isPending, hasAccounts }
}
